import { productVisualPimCompletionOutcomeSchema } from "./product-visual-pim-completion"
import { z } from "zod"

// Shared liveness contract for the background sync jobs (offer-sync,
// orders-sync — API-owned; the products full resnapshot, which since issue #312
// runs in its own Cloud Run Job process). Each persists one row per job, and each
// job's runner touches that row's `updated_at` on a fixed cadence for as long as
// its process is alive — independent of work progress. That the runner may be a
// different process from the API is exactly why the row, not a promise, is the
// contract. That independence matters: a job queued
// behind a shared Mirakl credential, deep in a 429/timeout retry sequence, or
// inside a multi-minute rate-limited enrichment batch makes no visible progress
// for a long time while being perfectly alive. Only the touch cadence answers
// "is the process behind this row still there?".
//
// One deliberate exception: the products sync progress-gates its keep-alive
// (product-sync-job.ts), pausing touches when the walk makes no progress. Do NOT
// read that as "every products wait is short enough to fit the gate" — it isn't.
// Consecutive units on that path are not separated by a beat, so the longest
// unbeaten gap composes (Magento fetch ≤5 min + an untimed pool-acquire wait + a
// chunk transaction ~210s, itself unbounded rather than capped: issue #311's
// CHUNK_STATEMENT_TIMEOUT_MS caps its one heavy statement, not the transaction, and
// the commit phase carries no deadline at all), and under pool contention
// it can exceed PRODUCT_SYNC_PROGRESS_STALL_MS outright. Blowing that window
// pauses touching; a reap still needs SYNC_JOB_STALL_MS of frozen updated_at.
// Scheduled product work is then interrupted by the minutely dispatcher before
// a successor adopts new ownership. A standalone local recovery waits for its
// next acquire or boot reconciliation. Since issue #312, an API restart leaves
// a HEALTHY resnapshot alone because that walk runs in its own Cloud Run process
// and continues crossing checkpoints.
// PRODUCT_SYNC_PROGRESS_STALL_MS owns that whole
// derivation; read it there before changing any of it, and note it is trigger
// scarcity doing the work, not a comfortable margin. Do not copy that gating onto
// orders/offers, whose Mirakl-
// style 429 retries can honestly stall for longer than the threshold (an
// alive-but-rate-limited job would be falsely reaped) — and do not "fix"
// products back to unconditional touching.
//
// Every consumer — the API's start dedup, the CLI pollers, the web pollers —
// reads staleness through the SAME threshold below, so "when is a `running`
// row dead?" has exactly one answer instead of hand-agreed literals.

/** Cadence of a job runner's `updated_at` touch while the job is alive. */
export const SYNC_JOB_TOUCH_INTERVAL_MS = 60 * 1000

/** The full products resnapshot's daily cron interval. This constant only derives
 * the bounded per-store resume age below. */
export const PRODUCTS_RESNAPSHOT_CYCLE_MS = 24 * 60 * 60 * 1000

/** How old a carried per-store resnapshot result may be and still be resumed from
 *  (issue #312's per-store resumability).
 *
 *  **Measured against the instant that store's walk COMPLETED**, never against the
 *  age of the job row carrying it: a resumed entry is re-persisted and re-emitted by
 *  every later execution, so each night's row looks fresh while its contents can be
 *  arbitrarily old. Bounding the row bounds nothing about the data. See
 *  `resumableStoreResults` in `apps/api/src/modules/products/product-sync-job.ts`.
 *
 *  **Half the cycle, deliberately, and the clearance is the reason.** The bound has
 *  to answer "was this store repaired as part of the CURRENT cycle?", and a bound
 *  sitting on the cron period cannot: nightly instants land 24h ± jitter apart, and
 *  on the spring CET→CEST night exactly 23h apart, so the same store would be
 *  carried or re-walked by a coin flip. At 12h the boundary is never straddled from
 *  either side — a same-night operator re-run is comfortably inside, and the previous night is
 *  comfortably outside. The cost of being outside is one store re-walked (~20 min),
 *  which is the nightly repair doing its job. */
export const PRODUCTS_RESNAPSHOT_RESUME_MAX_AGE_MS = PRODUCTS_RESNAPSHOT_CYCLE_MS / 2

/** A `running` row whose `updated_at` is older than this is dead: its runner
 *  missed five consecutive touches, which a live process cannot do (a touch is
 *  a single UPDATE; five straight misses means the process — or its DB access —
 *  is gone, and a job that can't reach the DB can't record a result either).
 *  Used two ways:
 *
 *  - **Server dedup** — a `running` row frozen past this window is superseded,
 *    so a genuinely-dead job never permanently blocks a re-run (belt-and-
 *    suspenders behind single-instance boot reconcile, and the only guard on a
 *    peer's crash in a multi-instance cluster).
 *  - **CLI/web pollers** — a poll aborts only when `updated_at` stalls this
 *    long, NEVER on a fixed wall-clock, so an honestly-long run still succeeds.
 */
export const SYNC_JOB_STALL_MS = 5 * SYNC_JOB_TOUCH_INTERVAL_MS

export const scheduledInfrastructureOutcomeSchema = z
  .object({
    version: z.literal(1),
    completed: z.boolean(),
    marketplaceFailures: z.array(z.string().min(1).max(100)).max(100),
    failedStages: z.array(z.string().min(1).max(100)).max(20),
  })
  .strict()

const productAuditLogGapsSchema = z
  .object({
    movedParents: z.number().int().nonnegative(),
    comparedParents: z.number().int().nonnegative(),
    gapParents: z.number().int().nonnegative(),
    unhashedParents: z.number().int().nonnegative(),
    ambiguousParents: z.number().int().nonnegative(),
    skipped: z.string().max(500).nullable(),
    errors: z.array(z.string().max(500)).max(20),
  })
  .strict()

const productAbsenceSchema = z
  .object({
    storeRows: z.number().int().nonnegative(),
    candidates: z.number().int().nonnegative(),
    flagged: z.number().int().nonnegative(),
    refused: z.boolean(),
    errors: z.array(z.string().max(500)).max(20),
  })
  .strict()

export const productScheduledStoreOutcomeSchema = z.discriminatedUnion("ok", [
  z
    .object({
      store: z.string().min(1).max(100),
      ok: z.literal(true),
      changed: z.number().int().nonnegative(),
      examined: z.number().int().nonnegative(),
      written: z.number().int().nonnegative(),
      missing: z.number().int().nonnegative(),
      elapsedMs: z.number().int().nonnegative(),
      resumed: z.boolean(),
      auditLogGaps: productAuditLogGapsSchema.optional(),
      absence: productAbsenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      store: z.string().min(1).max(100),
      ok: z.literal(false),
      error: z.string().min(1).max(500),
    })
    .strict(),
])

export const productScheduledOutcomeSchema = z
  .object({
    version: z.literal(1),
    kind: z.literal("products_sync"),
    mode: z.enum(["delta", "full"]),
    completed: z.boolean(),
    productJobId: z.string().max(200).nullable(),
    aggregate: z
      .object({
        storesTotal: z.number().int().nonnegative(),
        succeeded: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        changed: z.number().int().nonnegative(),
        examined: z.number().int().nonnegative(),
        written: z.number().int().nonnegative(),
        totalElapsedMs: z.number().int().nonnegative(),
      })
      .strict(),
    stores: z.array(productScheduledStoreOutcomeSchema).max(100),
  })
  .strict()

const productVisualPimAuditLocaleCoverageV1Schema = z
  .object({
    locale: z.string().min(1).max(20),
    examinedProducts: z.number().int().nonnegative(),
    responseRows: z.number().int().nonnegative(),
    compared: z.number().int().nonnegative(),
    differences: z.number().int().nonnegative(),
  })
  .strict()

const productVisualPimAuditLocaleCoverageV2Schema = z
  .object({
    locale: z.string().min(1).max(20),
    examinedProducts: z.number().int().nonnegative(),
    responseRows: z.number().int().nonnegative(),
    compared: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    mismatched: z.number().int().nonnegative(),
    unbaselined: z.number().int().nonnegative(),
    comparable: z.number().int().nonnegative(),
    baselineCoverage: z.number().min(0).max(1),
    driftRate: z.number().min(0).max(1),
  })
  .strict()

const productVisualPimAuditLocaleCoverageSchema =
  productVisualPimAuditLocaleCoverageV2Schema.extend({
    unexplainedUnbaselined: z.number().int().nonnegative(),
    explainedTerminalExceptions: z.number().int().nonnegative(),
  })

export const productVisualPimTerminalExceptionSchema = z
  .object({
    fillJobId: z.string().min(1).max(200),
    store: z.string().min(1).max(100),
    locale: z.string().min(1).max(20),
    magentoId: z.number().int().positive(),
    simpleSku: z.string().min(1).max(256),
    kind: z.literal("nutrition"),
    cause: z.string().min(1).max(100),
    evidence: z.string().min(1).max(500),
    terminalAttempt: z.number().int().min(1).max(10),
  })
  .strict()

const productVisualPimAuditFailureSchema = z
  .object({
    kind: z.enum(["pim_unavailable", "partial_response", "lease_lost", "enqueue_failed"]),
    locale: z.string().min(1).max(20).nullable(),
    store: z.string().min(1).max(100).nullable(),
    requestedProducts: z.number().int().nonnegative(),
    responseRows: z.number().int().nonnegative(),
  })
  .strict()

const productVisualPimAuditOutcomeV1Schema = z
  .object({
    version: z.literal(1),
    kind: z.literal("product_visual_pim_audit"),
    completed: z.boolean(),
    uniqueLocaleProducts: z.number().int().nonnegative(),
    compared: z.number().int().nonnegative(),
    differences: z.number().int().nonnegative(),
    differenceRate: z.number().min(0).max(1),
    enqueuedJobs: z.number().int().nonnegative(),
    enqueuedTargets: z.number().int().nonnegative(),
    localeCoverage: z.array(productVisualPimAuditLocaleCoverageV1Schema).max(100),
    failures: z.array(productVisualPimAuditFailureSchema).max(20),
    durationMs: z.number().int().nonnegative(),
  })
  .strict()

const productVisualPimAuditOutcomeV2Schema = z
  .object({
    version: z.literal(2),
    kind: z.literal("product_visual_pim_audit"),
    completed: z.boolean(),
    uniqueLocaleProducts: z.number().int().nonnegative(),
    compared: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    mismatched: z.number().int().nonnegative(),
    unbaselined: z.number().int().nonnegative(),
    comparable: z.number().int().nonnegative(),
    baselineCoverage: z.number().min(0).max(1),
    driftRate: z.number().min(0).max(1),
    enqueuedJobs: z.number().int().nonnegative(),
    enqueuedTargets: z.number().int().nonnegative(),
    localeCoverage: z.array(productVisualPimAuditLocaleCoverageV2Schema).max(100),
    failures: z.array(productVisualPimAuditFailureSchema).max(20),
    durationMs: z.number().int().nonnegative(),
  })
  .strict()

export const productVisualPimAuditOutcomeSchema = z
  .object({
    version: z.literal(3),
    kind: z.literal("product_visual_pim_audit"),
    completed: z.boolean(),
    uniqueLocaleProducts: z.number().int().nonnegative(),
    compared: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    mismatched: z.number().int().nonnegative(),
    unbaselined: z.number().int().nonnegative(),
    unexplainedUnbaselined: z.number().int().nonnegative(),
    explainedTerminalExceptions: z.number().int().nonnegative(),
    comparable: z.number().int().nonnegative(),
    baselineCoverage: z.number().min(0).max(1),
    driftRate: z.number().min(0).max(1),
    enqueuedJobs: z.number().int().nonnegative(),
    enqueuedTargets: z.number().int().nonnegative(),
    localeCoverage: z.array(productVisualPimAuditLocaleCoverageSchema).max(100),
    terminalExceptions: z.array(productVisualPimTerminalExceptionSchema).max(100),
    terminalExceptionsTruncated: z.boolean(),
    failures: z.array(productVisualPimAuditFailureSchema).max(20),
    durationMs: z.number().int().nonnegative(),
  })
  .strict()

/** Historical audit outcomes remain readable after the classifier contract
 * moved from a binary difference to explicit baseline state. */
export const persistedProductVisualPimAuditOutcomeSchema = z.union([
  productVisualPimAuditOutcomeV1Schema,
  productVisualPimAuditOutcomeV2Schema,
  productVisualPimAuditOutcomeSchema,
])

const productValueEventPassOutcomeV1Schema = z
  .object({
    version: z.literal(1),
    kind: z.literal("product_values_events"),
    completed: z.literal(true),
    applied: z.number().int().nonnegative(),
    eligibleRemaining: z.number().int().nonnegative(),
    pendingRemaining: z.number().int().nonnegative(),
    mirrorEventsHandled: z.number().int().nonnegative(),
    mirrorPageLimitReached: z.boolean(),
  })
  .strict()

/** Completed means this bounded pass completed, not that every domain queue is empty. */
export const productValueEventPassOutcomeSchema = z
  .object({
    version: z.literal(2),
    kind: z.literal("product_values_events"),
    completed: z.literal(true),
    applied: z.number().int().nonnegative(),
    eligibleRemaining: z.number().int().nonnegative(),
    pendingRemaining: z.number().int().nonnegative(),
    mirrorEventsHandled: z.number().int().nonnegative(),
    mirrorPageLimitReached: z.boolean(),
    mirrorSupersessionsBlocked: z.number().int().nonnegative(),
    mirrorSupersessionRepairRemaining: z.boolean(),
    // Older retained v2 outcomes predate obsolete-application recovery.
    obsoleteApplicationsSettled: z.number().int().nonnegative().optional(),
    obsoleteApplicationRepairRemaining: z.boolean().optional(),
  })
  .strict()

export const persistedProductValueEventPassOutcomeSchema = z.union([
  productValueEventPassOutcomeV1Schema,
  productValueEventPassOutcomeSchema,
])

export const productVisualEventPassOutcomeSchema = z
  .object({
    version: z.literal(1),
    kind: z.literal("product_visual_events"),
    completed: z.literal(true),
    pagesAdvanced: z.number().int().nonnegative(),
    jobsStarted: z.number().int().nonnegative(),
    pendingJobs: z.number().int().nonnegative(),
    pimEventsAttempted: z.number().int().nonnegative(),
    continuationRequired: z.boolean(),
  })
  .strict()

/** Persisted scheduled outcomes cross the API/CLI boundary. Keep the generic
 * infrastructure failure and the product report in one owned runtime union. */
export const scheduledJobOutcomeSchema = z.union([
  scheduledInfrastructureOutcomeSchema,
  persistedProductValueEventPassOutcomeSchema,
  productVisualEventPassOutcomeSchema,
  productScheduledOutcomeSchema,
  persistedProductVisualPimAuditOutcomeSchema,
  productVisualPimCompletionOutcomeSchema,
])

export type ScheduledInfrastructureOutcome = z.infer<typeof scheduledInfrastructureOutcomeSchema>
export type ProductScheduledStoreOutcome = z.infer<typeof productScheduledStoreOutcomeSchema>
export type ProductScheduledOutcome = z.infer<typeof productScheduledOutcomeSchema>
export type ProductVisualPimAuditOutcome = z.infer<typeof productVisualPimAuditOutcomeSchema>
export type ProductVisualPimTerminalException = z.infer<
  typeof productVisualPimTerminalExceptionSchema
>
export type ScheduledJobOutcome = z.infer<typeof scheduledJobOutcomeSchema>
